import type { CodeMigration } from './types.js';

/**
 * Retroactive migration: install the `postinstall` patcher that fixes the
 * upstream Tailwind v4 parser bug in `@atlashub/smartstack/theme.css`.
 *
 * The Vite plugin shipped by the previous Tailwind v4 migration
 * (`fixSmartstackThemeComment`) does NOT fix the problem reliably because
 * Tailwind v4 reads `@import`-ed CSS files directly from disk before any
 * Vite `load()` hook fires. Patching the file on disk via `postinstall` is
 * the only fully reliable approach.
 *
 * This migration targets two files per frontend project:
 *
 * 1. `web/<name>-web/package.json` — adds `postinstall` script + ensures the
 *    target path is the canonical one. Idempotent.
 * 2. `web/<name>-web/scripts/patch-smartstack-theme.cjs` — written via a
 *    companion FileMigration once the script template is in place. (See
 *    note below — the existing CodeMigration shape only supports detecting
 *    and transforming EXISTING files, so we use a sentinel pattern that
 *    matches a non-existent path; the real provisioning happens in
 *    upgrade.ts which checks for this script and creates it from
 *    `templates/project/patch-smartstack-theme.cjs.template` when missing.)
 */
export const smartstackThemePostinstallMigration: CodeMigration = {
  axis: 'cli',
  fromVersion: '1.0.0',
  toVersion: '5.0.0',
  description: 'Install postinstall patcher for @atlashub/smartstack/theme.css (Tailwind v4 parser workaround)',

  migrations: [
    {
      pattern: '**/web/**/package.json',
      description: 'Add `postinstall` hook calling scripts/patch-smartstack-theme.cjs',

      detect: (content: string): boolean => {
        try {
          const pkg = JSON.parse(content) as { scripts?: Record<string, string> };
          // Only target frontend package.jsons (those that depend on @atlashub/smartstack)
          const isSmartStackFrontend = content.includes('@atlashub/smartstack');
          if (!isSmartStackFrontend) return false;
          const currentPostinstall = pkg.scripts?.postinstall ?? '';
          return !currentPostinstall.includes('patch-smartstack-theme.cjs');
        } catch {
          return false;
        }
      },

      transform: (content: string): string => {
        const pkg = JSON.parse(content) as {
          scripts?: Record<string, string>;
          [k: string]: unknown;
        };
        pkg.scripts = pkg.scripts ?? {};
        const existing = pkg.scripts.postinstall;
        const ourCmd = 'node scripts/patch-smartstack-theme.cjs';
        // Preserve any prior postinstall command by chaining it.
        if (existing && existing.trim() && !existing.includes('patch-smartstack-theme.cjs')) {
          pkg.scripts.postinstall = `${ourCmd} && ${existing}`;
        } else {
          pkg.scripts.postinstall = ourCmd;
        }
        return JSON.stringify(pkg, null, 2);
      },
    },
  ],
};
